/**
 * Sticky Product Navigation
 * Enhanced mobile-first sticky navigation for product pages
 */

class StickyProductNav {
  constructor() {
    this.stickyNav = document.getElementById('sticky-product-nav');
    this.productSection = document.querySelector('.product-view, .product-essential');
    this.lastScrollY = window.scrollY;
    this.isVisible = false;
    this.ticking = false;
    this.scrollThreshold = 100; // Minimum scroll distance to trigger show/hide
    this.hideTimeout = null;
    
    if (this.stickyNav && this.productSection) {
      this.init();
    }
  }
  
  init() {
    this.bindEvents();
    this.setupSectionNavigation();
    this.handleInitialState();
  }
  
  bindEvents() {
    // Throttled scroll handler
    window.addEventListener('scroll', () => this.requestTick(), { passive: true });
    
    // Resize handler for responsive adjustments
    window.addEventListener('resize', () => this.handleResize(), { passive: true });
    
    // Touch events for mobile
    if ('ontouchstart' in window) {
      this.bindTouchEvents();
    }
  }
  
  bindTouchEvents() {
    let touchStartY = 0;
    let touchEndY = 0;
    
    document.addEventListener('touchstart', (e) => {
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });
    
    document.addEventListener('touchend', (e) => {
      touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe(touchStartY, touchEndY);
    }, { passive: true });
  }
  
  handleSwipe(startY, endY) {
    const swipeThreshold = 50;
    const diff = startY - endY;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiping up - hide nav
        this.hideNav();
      } else {
        // Swiping down - show nav if conditions met
        this.showNavIfNeeded();
      }
    }
  }
  
  requestTick() {
    if (!this.ticking) {
      requestAnimationFrame(() => this.updateStickyNav());
      this.ticking = true;
    }
  }
  
  updateStickyNav() {
    const currentScrollY = window.scrollY;
    const scrollDiff = Math.abs(currentScrollY - this.lastScrollY);
    
    // Only process if scroll difference is significant
    if (scrollDiff < 5) {
      this.ticking = false;
      return;
    }
    
    const productSectionRect = this.productSection.getBoundingClientRect();
    const productSectionBottom = this.productSection.offsetTop + this.productSection.offsetHeight;
    
    // Determine if we should show the nav
    const shouldShow = currentScrollY > productSectionBottom && currentScrollY > this.scrollThreshold;
    
    if (shouldShow) {
      // Show on scroll up, hide on scroll down
      if (currentScrollY < this.lastScrollY && !this.isVisible) {
        this.showNav();
      } else if (currentScrollY > this.lastScrollY && this.isVisible) {
        this.hideNav();
      }
    } else {
      // Above product section - always hide
      this.hideNav(true);
    }
    
    this.lastScrollY = currentScrollY;
    this.ticking = false;
  }
  
  showNav() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    this.stickyNav.style.display = 'block';
    // Force reflow
    this.stickyNav.offsetHeight;
    this.stickyNav.classList.add('show');
    this.isVisible = true;
    
    // Add body class for other elements to adjust
    document.body.classList.add('sticky-product-nav-visible');
  }
  
  showNavIfNeeded() {
    const currentScrollY = window.scrollY;
    const productSectionBottom = this.productSection.offsetTop + this.productSection.offsetHeight;
    
    if (currentScrollY > productSectionBottom && currentScrollY > this.scrollThreshold) {
      this.showNav();
    }
  }
  
  hideNav(immediate = false) {
    this.stickyNav.classList.remove('show');
    this.isVisible = false;
    document.body.classList.remove('sticky-product-nav-visible');
    
    if (immediate) {
      this.stickyNav.style.display = 'none';
    } else {
      // Hide after animation completes
      this.hideTimeout = setTimeout(() => {
        if (!this.isVisible) {
          this.stickyNav.style.display = 'none';
        }
      }, 300);
    }
  }
  
  handleInitialState() {
    // Check if we should show on page load (e.g., if user refreshes while scrolled)
    const currentScrollY = window.scrollY;
    const productSectionBottom = this.productSection.offsetTop + this.productSection.offsetHeight;
    
    if (currentScrollY > productSectionBottom && currentScrollY > this.scrollThreshold) {
      this.showNav();
    }
  }
  
  handleResize() {
    // Recalculate positions on resize
    this.lastScrollY = window.scrollY;
    this.updateStickyNav();
  }
  
  setupSectionNavigation() {
    const sectionBtns = this.stickyNav.querySelectorAll('.nav-section-btn');
    
    sectionBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollToSection(btn.dataset.target);
        
        // Add active state
        sectionBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
  }
  
  scrollToSection(target) {
    // Map target names to actual tab IDs and selectors
    const targetMap = {
      'description': {
        selectors: ['#product_tabs_description', '.description_tab', '[href="#product_tabs_description"]'],
        tabLink: '.description_tab a, [href="#product_tabs_description"]'
      },
      'review': {
        selectors: ['#product_tabs_tabreviews', '.reviews_tab', '[href="#product_tabs_tabreviews"]'],
        tabLink: '.reviews_tab a, [href="#product_tabs_tabreviews"]'
      },
      'shipping': {
        selectors: ['#tab8b43404e-c4ab-433b-9904-efcc15c6b1a6', '.html_tab', '[data-target*="shipping"]'],
        tabLink: '.html_tab a'
      }
    };
    
    const targetConfig = targetMap[target];
    let targetElement = null;
    
    if (targetConfig) {
      // Try to find the target element
      for (const selector of targetConfig.selectors) {
        targetElement = document.querySelector(selector);
        if (targetElement) break;
      }
      
      // If we found a tab link, click it to activate the tab
      const tabLink = document.querySelector(targetConfig.tabLink);
      if (tabLink) {
        tabLink.click();
        // Wait for tab to activate, then scroll
        setTimeout(() => {
          const activeTab = document.querySelector('.tab-content.active, .tab-content.show');
          if (activeTab) {
            this.scrollToElement(activeTab);
          }
        }, 100);
        return;
      }
    }
    
    // Fallback: try generic selectors
    if (!targetElement) {
      const selectors = [
        `#product_tabs_${target}`,
        `.${target}_tab`,
        `[data-section="${target}"]`,
        `.${target}`,
        `#${target}`
      ];
      
      for (const selector of selectors) {
        targetElement = document.querySelector(selector);
        if (targetElement) break;
      }
    }
    
    if (targetElement) {
      this.scrollToElement(targetElement);
    }
  }
  
  scrollToElement(element) {
    const offset = this.getScrollOffset();
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    
    window.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth'
    });
    
    // Highlight the target briefly
    this.highlightElement(element);
  }
  
  getScrollOffset() {
    const stickyHeader = document.querySelector('.header-container.sticky-header');
    let offset = 20; // Base offset
    
    if (stickyHeader) {
      offset += stickyHeader.offsetHeight;
    }
    
    return offset;
  }
  
  highlightElement(element) {
    element.style.transition = 'background-color 0.3s ease';
    element.style.backgroundColor = 'rgba(34, 37, 41, 0.05)';
    
    setTimeout(() => {
      element.style.backgroundColor = '';
      setTimeout(() => {
        element.style.transition = '';
      }, 300);
    }, 1000);
  }
}

// Enhanced scroll to add to cart function
function scrollToAddToCart() {
  const selectors = [
    '.product-options-bottom',
    '.add-to-cart-box',
    '.product-form',
    '.shopify-payment-button',
    '[data-add-to-cart]',
    '.btn-cart'
  ];
  
  let addToCartSection = null;
  
  for (const selector of selectors) {
    addToCartSection = document.querySelector(selector);
    if (addToCartSection) break;
  }
  
  if (addToCartSection) {
    const offset = 80; // Account for sticky elements
    const elementTop = addToCartSection.getBoundingClientRect().top + window.scrollY;
    
    window.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth'
    });
    
    // Find and highlight the add to cart button
    const addToCartBtn = addToCartSection.querySelector('.add-to-cart, .btn-cart, .shopify-payment-button__button');
    if (addToCartBtn) {
      // Pulse animation
      addToCartBtn.style.transform = 'scale(1.05)';
      addToCartBtn.style.boxShadow = '0 0 20px rgba(34, 37, 41, 0.3)';
      
      setTimeout(() => {
        addToCartBtn.style.transform = 'scale(1)';
        addToCartBtn.style.boxShadow = '';
      }, 300);
      
      // Focus for accessibility
      if (addToCartBtn.focus) {
        setTimeout(() => addToCartBtn.focus(), 500);
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Small delay to ensure all elements are rendered
  setTimeout(() => {
    window.stickyProductNav = new StickyProductNav();
  }, 100);
});

// Reinitialize on AJAX page loads (for SPA-like behavior)
document.addEventListener('shopify:section:load', function() {
  if (window.stickyProductNav) {
    window.stickyProductNav = new StickyProductNav();
  }
});

// Handle page visibility changes (mobile optimization)
document.addEventListener('visibilitychange', function() {
  if (document.hidden && window.stickyProductNav) {
    window.stickyProductNav.hideNav(true);
  }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StickyProductNav;
}